import { BaseDialog } from "@/lib/Components/Dialog/BaseDialog";
import { GenericForm } from "@/lib/Components/Form/Forms/GenericForm";
import {
  useGqlMutation,
  useSuspenseGqlQuery,
} from "@/lib/GraphQLCodegen/fetcher";
import { workOrderRecordQuery } from "@/app/ProDrivers/WorkOrders/GraphQL/workOrdersQuery";
import { useDialogState } from "@/lib/Components/Dialog/Hooks/useDialogState";
import {
  UpdateWorkOrderInput,
  SupplierNotificationSettingType,
} from "@/gql/graphql";
import { updateWorkOrderMutation } from "@/app/ProDrivers/WorkOrders/GraphQL/workOrdersMutations";
import { WorkOrderNotificationSettingForm } from "@/app/ProDrivers/WorkOrders/Components/Forms/WorkOrderNotificationSettingForm";
import { DialogSubmitButton } from "@/lib/Components/Dialog/Components/DialogSubmitButton";
import { z } from "zod";
import { toFormikValidationSchema } from "@/lib/Zod/zod-formik-adapter";

const validationSchema = z.object({
  notificationSettings: z.object({
    upsert: z.array(
      z.object({
        id: z.string().optional(),
        email: z.string().email(),
        type: z.nativeEnum(SupplierNotificationSettingType),
      }),
    ),
    delete: z.array(z.string()).optional(),
  }),
});

export function UpdateWorkOrderNotificationSettingsDialog({
  workOrderId,
}: {
  workOrderId: string;
}) {
  const { data } = useSuspenseGqlQuery(workOrderRecordQuery, {
    id: workOrderId,
  });
  const { close } = useDialogState();
  const { mutateAsync } = useGqlMutation(updateWorkOrderMutation);

  const workOrder = data.workOrder;
  const existingSettings = workOrder?.notificationSettings ?? [];

  const initialUpsertValues = existingSettings.map((setting) => ({
    id: setting.id,
    email: setting.email,
    type: setting.type,
  }));

  return (
    <BaseDialog
      title="Work Order Notifications"
      subtitle="Configure email notifications for work order events"
    >
      <GenericForm<UpdateWorkOrderInput>
        validationSchema={toFormikValidationSchema(validationSchema)}
        initialValues={{
          notificationSettings: {
            upsert: initialUpsertValues,
            delete: [],
          },
        }}
        onSubmit={async (values) => {
          await mutateAsync({
            id: workOrderId,
            input: values,
          });

          close();
        }}
      >
        <WorkOrderNotificationSettingForm />
        <DialogSubmitButton text="Update notifications" />
      </GenericForm>
    </BaseDialog>
  );
}
